import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine, BarChart, Bar, Legend, LabelList } from 'recharts';
import AlarmManagement from './AlarmManagement';
import HealthScore from './HealthScore';
import JiraTicketsSection from './JiraTicketsSection';
import KpiSection from './KpiSection';
import PredictiveMonitoringSection from './PredictiveMonitoringSection';
import IdleResourcesSection from './IdleResourcesSection';
import TopAlarmsSection from './TopAlarmsSection';
import AwsServiceIcon from './AwsServiceIcon';
import InfraHealthSummary from './InfraHealthSummary';
import ServiceTile from './ServiceTile';
import SummaryTile from './SummaryTile';
import CostKpiRow from './CostKpiRow';
import { computeInfraSummary, matchesInfraFilter } from './infraSummary';
import { getServiceStats } from './serviceStats';
import { getServiceCharts } from './serviceCharts';
import { serviceDisplayName } from './serviceMeta';
import { hasTeamIntegrations, isJiraEnabled } from '../config/teamIntegrations';
import { IESIntegrationDashboard } from './teams';
import { getDefaultRegion, availableRegions, accountMappings, toInternalTeamName } from '../config/teamConfig';
import './AccountDetail-enhanced.css';

// Get API URL from environment variable or use relative path for local development
const API_URL = process.env.REACT_APP_API_URL || '';

// Batch -> service keys, mirroring the backend INFRA_SERVICE_BATCHES in
// backend/lambda_function.py. Kept here (self-documenting) so it stays in sync
// with the backend and so the service->batch inverse map below can be derived
// by inversion, guaranteeing every batch key is covered.
const INFRA_BATCH_SERVICES = {
  alarms: ['alarms'],
  compute: ['ec2', 'lambda', 'ecs'],
  storage: ['s3', 'ecr', 'elasticache'],
  database: ['rds', 'dynamodb'],
  'networking-1': ['apigateway', 'elb'],
  'networking-2': ['vpc', 'cloudfront', 'waf'],
  'integration-1': ['sns', 'sqs', 'eventbridge'],
  'integration-2': ['stepfunctions', 'kinesis', 'glue'],
  monitoring: ['cloudwatch', 'guardduty'],
  security: ['secretsmanager', 'parameterstore', 'config'],
  ops: ['backup', 'codepipeline', 'mwaa', 'msk'],
  'security-2': ['acm', 'securityhub', 'accessanalyzer'],
  encryption: ['kms'],
  analytics: ['opensearch', 'appflow', 'inspector'],
  migration: ['transfer', 'dms'],
};

// Inverse map service key -> batch key, derived ONCE by inverting the object
// above. Used by the render to reveal each service group as its batch lands.
const INFRA_SERVICE_TO_BATCH = Object.entries(INFRA_BATCH_SERVICES).reduce(
  (acc, [batch, services]) => {
    services.forEach(svc => { acc[svc] = batch; });
    return acc;
  },
  {}
);

const AccountDetail = () => {
  const { teamName, environment } = useParams();
  const navigate = useNavigate();
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingInfra, setLoadingInfra] = useState(false);
  // Per-batch infra loading status: { [batchKey]: 'loading'|'done'|'error' }.
  // Drives per-group reveal so each service group lights up as its batch lands,
  // instead of hiding every group behind the single global loadingInfra flag.
  const [infraBatchStatus, setInfraBatchStatus] = useState({});
  const [error, setError] = useState(null);
  // Initialize selectedRegion based on team's default region
  const [selectedRegion, setSelectedRegion] = useState(() => getDefaultRegion(teamName));
  const [showAlarmDetails, setShowAlarmDetails] = useState(false);
  const [alarmDetails, setAlarmDetails] = useState(null);
  const [loadingAlarms, setLoadingAlarms] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [teamCostRows, setTeamCostRows] = useState([]);

  // Top 10 most frequently triggered alarms in the last 7 days, with their
  // root-cause analysis (S3 cache, refreshed weekly in prod / monthly in dev)
  const [topAlarms, setTopAlarms] = useState(null);
  // AWS Cost Explorer recommendations (S3-cached, team-access filtered by backend).
  // Used to enrich the Optimization Opportunities panel with real $ savings.
  // null = loading, { error: true } = unavailable, otherwise the API payload.
  const [awsRecommendations, setAwsRecommendations] = useState(null);
  const [awsRecommendationsStale, setAwsRecommendationsStale] = useState(false);
  // Show the floating quick-nav bar once the user scrolls past the header. Uses
  // position:fixed (immune to the overflow-x ancestors that break position:
  // sticky), so the feature hyperlinks are always reachable while scrolling.
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  // Infrastructure section client-side controls: free-text service search and an
  // All/Issues-only toggle.
  const [infraSearch, setInfraSearch] = useState('');
  const [infraFilter, setInfraFilter] = useState('all');

  useEffect(() => {
    const onScroll = () => setShowFloatingNav(window.scrollY > 220);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Get account ID from mapping
  const getAccountId = () => {
    // Map display name to internal key
    const internalName = toInternalTeamName(teamName);
    return accountMappings[internalName]?.[environment.toLowerCase()] || accountData?.accountId || '';
  };

  const currentAccountId = getAccountId();

  // Returns null (not 0) when a figure is absent or unparseable, so downstream
  // Number.isFinite guards can omit it. Coercing to 0 here turned an unavailable
  // forecast into a $0 bar on the By Environment chart, which reads as real data.
  const parseCostValue = (value) => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
      const normalized = value.replace(/[^0-9.-]/g, '');
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  // Update selected region when team changes
  useEffect(() => {
    const defaultRegion = getDefaultRegion(teamName);
    setSelectedRegion(defaultRegion);
  }, [teamName]);

  useEffect(() => {
    // Reset state when account changes
    setAccountData(null);
    setLoading(true);
    setLoadingInfra(false);
    setInfraBatchStatus({});
    setError(null);
    setAlarmDetails(null);
    setShowAlarmDetails(false);
    
    // Progressive loading: Cost first, then infrastructure
    fetchCostData();
    // fetchCostData is a stable closure recreated each render; we intentionally
    // re-run only when the account identity (team/env/region) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamName, environment, selectedRegion]);

  // Fetch the top 10 most frequently triggered alarms (last 7 days) for this
  // account. Served from an S3 cache, so it is fast and region-independent.
  useEffect(() => {
    let cancelled = false;
    const fetchTopAlarms = async () => {
      setTopAlarms(null);
      try {
        const response = await axios.get(
          `${API_URL}/api/account/${teamName}/${environment}/top-alarms`,
          { timeout: 15000 }
        );
        if (!cancelled) {
          setTopAlarms(response.data);
        }
      } catch (error) {
        console.error('Error fetching top alarms:', error);
        if (!cancelled) {
          setTopAlarms({ alarms: [] });
        }
      }
    };
    fetchTopAlarms();
    return () => {
      cancelled = true;
    };
  }, [teamName, environment]);

  // Fetch AWS Cost Explorer recommendations from the shared S3-cached endpoint.
  // This endpoint already applies team-access filtering on the backend and does
  // NOT trigger a live Cost Explorer call, so it satisfies the cached/daily
  // cost model and access-control requirements. We filter to the selected
  // account client-side when rendering the Optimization panel.
  useEffect(() => {
    let cancelled = false;
    const fetchAwsRecommendations = async () => {
      setAwsRecommendations(null);
      setAwsRecommendationsStale(false);
      try {
        // Opt into the Trusted Advisor rows. The overall dashboard calls this
        // endpoint without the flag and keeps its original payload shape.
        const response = await axios.get(`${API_URL}/api/recommendations`, {
          params: { includeTrustedAdvisor: 'true' },
          timeout: 15000,
        });
        if (cancelled) return;
        const data = response.data || {};
        setAwsRecommendations(data);
        // Flag data older than 7 days as potentially stale.
        const lastUpdatedStr = data.lastUpdated || data.timestamp;
        if (lastUpdatedStr) {
          const lastUpdatedMs = new Date(lastUpdatedStr).getTime();
          if (Number.isFinite(lastUpdatedMs)) {
            const ageDays = (Date.now() - lastUpdatedMs) / (1000 * 60 * 60 * 24);
            setAwsRecommendationsStale(ageDays > 7);
          }
        }
      } catch (err) {
        console.error('Error fetching AWS cost recommendations:', err);
        if (!cancelled) {
          // Mark as unavailable so the panel can fall back to count hints and
          // show the "savings data unavailable" indicator.
          setAwsRecommendations({ error: true });
        }
      }
    };
    fetchAwsRecommendations();
    return () => {
      cancelled = true;
    };
  }, [teamName, environment]);

  useEffect(() => {
    const fetchTeamCollectiveCosts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/metrics/overall`);
        const accountDetails = response.data?.accountDetails || [];
        const normalizedTeam = toInternalTeamName(teamName);

        const envOrder = { DEV: 1, QA: 2, ACC: 3, PROD: 4, SHARED: 5, SANDBOX: 6 };
        const rows = accountDetails
          .filter((account) => account.team === normalizedTeam)
          .map((account) => ({
            environment: (account.environment || '').toUpperCase(),
            accountId: account.accountId || '-',
            lastMonth: parseCostValue(account.lastMonth),
            currentMonth: parseCostValue(account.currentMonth),
            forecast: parseCostValue(account.forecast)
          }))
          .sort((a, b) => (envOrder[a.environment] || 99) - (envOrder[b.environment] || 99));

        setTeamCostRows(rows);
      } catch (error) {
        console.error('Error fetching team collective costs:', error);
        setTeamCostRows([]);
      }
    };

    fetchTeamCollectiveCosts();
  }, [teamName]);

  // Fetch this account's monthly spend trend from the S3-cached cost-history
  // endpoint. No live Cost Explorer call. On any error or an empty trend we
  // leave the state empty so the chart is omitted rather than showing a stub.
  // Add/remove body class when loading
  useEffect(() => {
    if (loading) {
      document.body.classList.add('loading-active');
    } else {
      document.body.classList.remove('loading-active');
    }
    return () => {
      document.body.classList.remove('loading-active');
    };
  }, [loading]);

  const fetchCostData = async () => {
    setError(null);
    try {
      console.log(`Fetching COST data for ${teamName}/${environment} in ${selectedRegion}`);
      
      // PHASE 1: Fetch cost data quickly from S3 cache
      const response = await axios.get(`${API_URL}/api/account/${teamName}/${environment}`, {
        params: {
          region: selectedRegion,
          dataType: 'cost'  // Only cost data - fast from S3 cache
        },
        timeout: 15000 // 15 second timeout for cost data
      });
      
      console.log('Cost data response:', response.data);
      
      if (response.data && Object.keys(response.data).length > 0) {
        setAccountData(response.data);
        setLastUpdated(new Date());
        setLoading(false);
        
        // PHASE 2: Start loading infrastructure data in background
        fetchInfrastructureData();
      } else {
        // Fall back to loading all data together if cost-only fails
        console.log('Cost data empty, falling back to full data fetch');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error fetching cost data:', error);
      // Fall back to loading all data together
      console.log('Cost fetch failed, falling back to full data fetch');
      fetchAllData();
    }
  };

  // Infrastructure is fetched LIVE and in BATCHES by service group so a very
  // large account never exceeds API Gateway's 29s limit. Each batch returns a
  // few services' live data fast; we merge each batch into state as it arrives
  // so the service tiles light up progressively. Batch keys MUST match the
  // backend INFRA_SERVICE_BATCHES in lambda_function.py.
  const INFRA_BATCHES = [
    'alarms',
    'compute', 'storage', 'database',
    'networking-1', 'networking-2',
    'integration-1', 'integration-2',
    'monitoring', 'security', 'ops',
    'security-2', 'encryption', 'analytics', 'migration',
  ];
  const INFRA_BATCH_CONCURRENCY = 3; // fire a few in parallel, gentle on AWS APIs

  const fetchInfrastructureData = async () => {
    setLoadingInfra(true);
    // Mark every batch 'loading' up front so each service group shows its
    // placeholder until that batch lands, then flips to 'done'/'error'.
    setInfraBatchStatus(Object.fromEntries(INFRA_BATCHES.map(b => [b, 'loading'])));

    const fetchBatch = async (batch) => {
      try {
        const response = await axios.get(`${API_URL}/api/account/${teamName}/${environment}`, {
          params: {
            region: selectedRegion,
            dataType: 'infrastructure',
            serviceBatch: batch,
          },
          timeout: 29000, // under API Gateway's 29s hard limit per call
        });
        const data = response.data;
        if (data && Object.keys(data).length > 0) {
          // Merge ONLY this batch's keys so other batches' tiles aren't clobbered.
          setAccountData(prevData => ({
            ...prevData,
            ...data,
            // Preserve cost fields from the earlier cost-only fetch.
            costBreakdown: prevData?.costBreakdown ?? data.costBreakdown,
            currentCost: prevData?.currentCost ?? data.currentCost,
            lastMonth: prevData?.lastMonth ?? data.lastMonth,
            forecast: prevData?.forecast ?? data.forecast,
          }));
          setLastUpdated(new Date());
        }
        // This batch landed: reveal its service groups.
        setInfraBatchStatus(prev => ({ ...prev, [batch]: 'done' }));
      } catch (error) {
        // One batch failing (e.g. a slow service group) must not block the rest.
        console.error(`Infrastructure batch '${batch}' failed:`, error);
        setInfraBatchStatus(prev => ({ ...prev, [batch]: 'error' }));
      }
    };

    try {
      console.log(`Fetching INFRASTRUCTURE (batched) for ${teamName}/${environment} in ${selectedRegion}`);
      // Run batches with a small concurrency window so tiles stream in while we
      // avoid hammering the AWS APIs with every group at once.
      const queue = [...INFRA_BATCHES];
      const runWorker = async () => {
        while (queue.length > 0) {
          const batch = queue.shift();
          // eslint-disable-next-line no-await-in-loop
          await fetchBatch(batch);
        }
      };
      const workers = Array.from(
        { length: Math.min(INFRA_BATCH_CONCURRENCY, INFRA_BATCHES.length) },
        () => runWorker()
      );
      await Promise.all(workers);
    } catch (error) {
      console.error('Error fetching infrastructure data:', error);
      console.log('Infrastructure batched fetch encountered errors, continuing with cost data only');
    } finally {
      setLoadingInfra(false);
    }
  };

  const fetchAllData = async () => {
    // Fallback: Fetch all data at once (original behavior)
    setError(null);
    try {
      console.log(`Fetching ALL data for ${teamName}/${environment} in ${selectedRegion}`);
      const response = await axios.get(`${API_URL}/api/account/${teamName}/${environment}`, {
        params: {
          region: selectedRegion
        },
        timeout: 90000 // 90 second timeout
      });
      console.log('All data response:', response.data);
      
      if (response.data && Object.keys(response.data).length > 0) {
        setAccountData(response.data);
        setLastUpdated(new Date());
      } else {
        setError('No data returned from server. Please try refreshing.');
      }
    } catch (error) {
      console.error('Error fetching all data:', error);
      if (error.code === 'ECONNABORTED') {
        setError('Request timed out. The server is taking too long to respond. Please try again.');
      } else if (error.response?.status === 504) {
        setError('Gateway timeout. Infrastructure data collection is taking longer than expected. Please try again.');
      } else if (error.response?.status === 500) {
        setError('Server error occurred. Please try again in a moment.');
      } else {
        setError(error.response?.data?.message || error.message || 'Failed to load account data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Keep the old function name for retry button compatibility
  const fetchAccountData = fetchAllData;

  const fetchAlarmDetails = async () => {
    if (!accountData?.alarms || accountData.alarms.alarm === 0) {
      return; // No alarms to fetch
    }

    setLoadingAlarms(true);
    setShowAlarmDetails(true);

    try {
      console.log(`Fetching alarm details for ${teamName}/${environment} in ${selectedRegion}`);
      const response = await axios.get(`${API_URL}/api/account/${teamName}/${environment}/alarms`, {
        params: {
          region: selectedRegion
        }
      });
      console.log('Alarm details response:', response.data);
      setAlarmDetails(response.data);
    } catch (error) {
      console.error('Error fetching alarm details:', error);
      setAlarmDetails({ error: 'Failed to load alarm details' });
    } finally {
      setLoadingAlarms(false);
    }
  };

  const closeAlarmDetails = () => {
    setShowAlarmDetails(false);
    setAlarmDetails(null);
  };

  // Build the Optimization Opportunities model from the S3-cached AWS Cost
  // Explorer recommendations, filtered to the selected account. Falls back to
  // heuristic estimates (accountData.recommendations) and finally to count-only
  // hints. See spec: optimization-savings-amounts.
  //
  // NOTE: This hook MUST run on every render (before any early return) so the
  // number of hooks stays constant. Moving it below the `if (!accountData)`
  // early return caused React error #310 (hooks order mismatch). All reads of
  // accountData below use optional chaining so it is null-safe on first render.
  const optimizationModel = useMemo(() => {
    // Round half-up to two decimals (avoids banker's rounding surprises).
    const roundHalfUp2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

    const accountId = currentAccountId;
    const recsPayloadError = awsRecommendations && awsRecommendations.error === true;
    const recsLoading = awsRecommendations === null;

    // --- AWS Cost Explorer recommendations for THIS account ---
    const allAwsRecs = (awsRecommendations && Array.isArray(awsRecommendations.recommendations))
      ? awsRecommendations.recommendations
      : [];

    const matchesAccount = (rec) =>
      accountId && String(rec.accountId || '') === String(accountId);
    const matchesRegion = (rec) =>
      rec.isGlobal === true || !rec.region || rec.region === 'global' || rec.region === selectedRegion;

    const awsRecs = allAwsRecs.filter(
      (rec) =>
        rec.source === 'AWS Cost Explorer' &&
        Number(rec.savings) > 0 &&
        matchesAccount(rec) &&
        matchesRegion(rec)
    );

    // De-duplicate account-level recommendations using the same first-occurrence
    // key the backend uses (accountId|title|service) so aggregate totals count
    // each recommendation exactly once.
    const seen = new Set();
    const dedupedAwsRecs = [];
    for (const rec of awsRecs) {
      const key = `${rec.accountId}|${rec.title}|${rec.service}`;
      if (seen.has(key)) continue;
      seen.add(key);
      dedupedAwsRecs.push(rec);
    }

    // Sort by savings descending for display.
    const sortedAwsRecs = [...dedupedAwsRecs].sort(
      (a, b) => Number(b.savings) - Number(a.savings)
    );

    // Aggregate total (round half-up to 2 decimals).
    const aggregateSavings = roundHalfUp2(
      sortedAwsRecs.reduce((sum, rec) => sum + Number(rec.savings || 0), 0)
    );

    // rightsizingEstimates is computed further below; its aggregate is derived after
    // it is built. Placeholder declared here and assigned before return.
    let rightsizingEstimateTotal = 0;

    // Map an AWS rec to an opportunity type for matching against count hints.
    const awsTypeOf = (rec) => {
      if (rec.category === 'AWS Rightsizing') return 'ec2-rightsizing';
      if (
        rec.title === 'AWS: Reserved Instance Opportunity' ||
        rec.title === 'AWS: Savings Plan Opportunity'
      ) {
        return 'reserved-instance';
      }
      return 'general';
    };
    const awsTypesPresent = new Set(sortedAwsRecs.map(awsTypeOf));

    // --- Heuristic estimates (percentage-of-cost) for THIS account ---
    // These carry no `source` field. Index by service/title keywords so we can
    // attach an "Estimate" figure to a count hint when AWS has nothing.
    const heuristics = Array.isArray(accountData?.recommendations)
      ? accountData.recommendations
      : [];
    const heuristicFor = (predicate) => {
      const match = heuristics.find((r) => !r.source && predicate(r));
      return match && Number(match.savings) > 0 ? Number(match.savings) : null;
    };
    const lowCpuEstimate = heuristicFor(
      (r) => (r.title || '').toLowerCase().includes('low cpu')
    );
    const stoppedEstimate = heuristicFor(
      (r) => (r.title || '').toLowerCase().includes('stopped')
    );

    // Concrete instance-type-selection recommendations (metrics "Estimate" source):
    // e.g. "Right-size m5.2xlarge → m5.xlarge". These name a specific target type and
    // carry a quantified $ saving. Rendered as their own opportunity line items with
    // an "Estimate" label. Only used when AWS Cost Explorer has no rightsizing rec
    // (which is authoritative and rendered via awsRecs). Sorted by savings desc.
    //
    // We merge TWO sources so the account card shows the SAME detailed per-instance
    // recommendations the Dashboard shows for this account:
    //   1. accountData.recommendations  — the per-account /api/account payload heuristics
    //   2. the shared /api/recommendations dataset (allAwsRecs), filtered to THIS
    //      account + region. These carry source='Metrics Analysis', category='Right-sizing'
    //      and are exactly the rows the Dashboard "Cost Optimization Recommendations"
    //      table renders. Previously these were dropped here, so the account card fell
    //      back to the generic "N EC2 low-CPU — No data" hint.
    const isRightsizingRec = (r) =>
      (r.category === 'Right-sizing' || /^right-size /i.test(r.title || '')) &&
      (r.currentType || r.targetType || /→|->/.test(r.title || '')) &&
      Number(r.savings) > 0;

    // Match on ACCOUNT only (not region) for the detailed metrics recs: the
    // account page is scoped to one account, and the shared dataset tags each
    // rec with the concrete region it was collected in. Filtering by the page's
    // currently-selected region would silently hide this account's rightsizing
    // recs found in other regions - which is exactly the "all recommendations
    // not showing" symptom. Showing every rec for THIS account is correct.
    const metricsRightsizingFromShared = allAwsRecs.filter(
      (r) =>
        (r.source === 'Metrics Analysis' || !r.source) &&
        isRightsizingRec(r) &&
        matchesAccount(r)
    );

    const rightsizingFromAccount = heuristics
      .filter((r) => !r.source || r.source === 'Metrics Analysis')
      .filter(isRightsizingRec);

    // Merge + de-duplicate on title + region, so the same instance-type pair
    // found in two different regions is kept as two line items (region is part
    // of the identity now that we match across regions). The per-account
    // heuristics carry no region, so they fall back to title-only keys.
    const seenRs = new Set();
    const rightsizingEstimates = [...metricsRightsizingFromShared, ...rightsizingFromAccount]
      .filter((r) => {
        const key = `${(r.title || '').trim().toLowerCase()}|${r.region || ''}`;
        if (seenRs.has(key)) return false;
        seenRs.add(key);
        return true;
      })
      .sort((a, b) => Number(b.savings) - Number(a.savings));

    // Estimate-based aggregate, only meaningful when we surface estimate rightsizing
    // recs (i.e. AWS Cost Explorer has no authoritative rightsizing rec).
    rightsizingEstimateTotal = roundHalfUp2(
      rightsizingEstimates.reduce((sum, rec) => sum + Number(rec.savings || 0), 0)
    );

    // --- Other recommendations for THIS account ---
    // The overall dashboard shows EVERY recommendation category (e.g. S3
    // "Storage Optimization" lifecycle policies), but the two buckets above only
    // surface AWS Cost Explorer recs and EC2 rightsizing. Any other category was
    // silently dropped at account level, so a real saving shown on the overall
    // dashboard was invisible here. Capture the remaining positive-savings recs
    // for this account (matched on account, not region, so a rec collected in a
    // different region than the one selected is still shown), excluding the ones
    // already covered by awsRecs and the rightsizing estimates.
    const shownKeys = new Set([
      ...sortedAwsRecs.map((r) => `${r.title}|${r.service}|${r.region || ''}`),
      ...rightsizingEstimates.map((r) => `${r.title}|${r.service}|${r.region || ''}`),
    ]);
    const otherRecsSeen = new Set();
    const otherRecs = allAwsRecs
      .filter(
        (r) =>
          matchesAccount(r) &&
          Number(r.savings) > 0 &&
          r.source !== 'AWS Cost Explorer' &&
          !isRightsizingRec(r)
      )
      .filter((r) => {
        const key = `${r.title}|${r.service}|${r.region || ''}`;
        if (shownKeys.has(key) || otherRecsSeen.has(key)) return false;
        otherRecsSeen.add(key);
        return true;
      })
      .sort((a, b) => Number(b.savings) - Number(a.savings));
    const otherRecsTotal = roundHalfUp2(
      otherRecs.reduce((sum, rec) => sum + Number(rec.savings || 0), 0)
    );

    // --- AWS Trusted Advisor cost checks for THIS account ---
    // Reported as its own source, never merged into combinedSavings: Trusted
    // Advisor's checks overlap Cost Explorer's (its low-utilization-EC2 check
    // covers the same instances as CE rightsizing, and it has its own Savings
    // Plans / RI checks), so one summed figure would double count. These recs
    // carry savings=0 and the real figure in potentialSavings.
    const taSeen = new Set();
    const taRecs = allAwsRecs
      .filter((r) => r.source === 'AWS Trusted Advisor' && matchesAccount(r))
      .filter((r) => Number(r.potentialSavings) > 0)
      .filter((r) => {
        const key = `${r.title}|${r.checkId || ''}`;
        if (taSeen.has(key)) return false;
        taSeen.add(key);
        return true;
      })
      .sort((a, b) => Number(b.potentialSavings) - Number(a.potentialSavings));
    const taSavingsTotal = roundHalfUp2(
      taRecs.reduce((sum, rec) => sum + Number(rec.potentialSavings || 0), 0)
    );

    // --- Count hints ---
    const lowCpuCount = accountData?.ec2?.lowCpuInstances || 0;
    const oldCount = accountData?.ec2?.oldInstances || 0;
    const stoppedCount = accountData?.ec2?.stopped || 0;

    // Combined headline total. The Estimate rightsizing recs are only RENDERED
    // when AWS Cost Explorer has no rightsizing rec of its own (so we never show
    // two recs for the same opportunity). Mirror that here so the headline total
    // equals the sum of the line items actually displayed below.
    const estimateShown = !awsTypesPresent.has('ec2-rightsizing')
      ? rightsizingEstimateTotal
      : 0;
    // Headline total includes the authoritative AWS Cost Explorer recs, the
    // estimate rightsizing recs actually rendered, AND the other-category recs
    // (e.g. S3 storage-optimization) now surfaced for every account.
    const combinedSavings = roundHalfUp2(
      aggregateSavings + estimateShown + otherRecsTotal
    );

    return {
      recsLoading,
      recsPayloadError,
      awsRecs: sortedAwsRecs,
      awsTypesPresent,
      aggregateSavings,
      lowCpuEstimate,
      stoppedEstimate,
      rightsizingEstimates,
      rightsizingEstimateTotal: estimateShown,
      otherRecs,
      otherRecsTotal,
      combinedSavings,
      taRecs,
      taSavingsTotal,
      lowCpuCount,
      oldCount,
      stoppedCount,
    };
  }, [awsRecommendations, accountData, currentAccountId, selectedRegion]);

  if (!accountData) {
    return (
      <div className="account-detail">
        {loading ? (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading {teamName} - {environment.toUpperCase()}</div>
            <div className="loading-subtext">Fetching cost data from cache...</div>
          </div>
        ) : error ? (
          <div className="error-container" style={{padding: '40px', textAlign: 'center'}}>
            <button className="back-button" onClick={() => navigate('/')}>
              ← Back to Dashboard
            </button>
            <div style={{marginTop: '60px'}}>
              <div style={{fontSize: '48px', marginBottom: '20px'}}>⚠️</div>
              <h3 style={{color: '#E53935', marginBottom: '12px'}}>Failed to Load Data</h3>
              <p style={{color: '#666', marginBottom: '20px'}}>{error}</p>
              <button 
                onClick={() => {
                  setLoading(true);
                  setError(null);
                  fetchAccountData();
                }}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#0051A3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🔄 Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="error-container" style={{padding: '40px', textAlign: 'center'}}>
            <button className="back-button" onClick={() => navigate('/')}>
              ← Back to Dashboard
            </button>
            <div style={{marginTop: '60px'}}>
              <div style={{fontSize: '48px', marginBottom: '20px'}}>📭</div>
              <h3 style={{color: '#666', marginBottom: '12px'}}>No Data Available</h3>
              <p style={{color: '#888', marginBottom: '20px'}}>No infrastructure data found for {teamName} - {environment}</p>
              <button 
                onClick={() => {
                  setLoading(true);
                  fetchAccountData();
                }}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#0051A3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Create a map of service costs from the cost breakdown
  const serviceCostMap = {};
  (accountData.costBreakdown || []).forEach(item => {
    const serviceName = item.service.toLowerCase();
    serviceCostMap[serviceName] = item.current;
  });

  // Define service sections with their cost mapping
  const serviceMapping = {
    'lambda': ['lambda', 'aws lambda'],
    'ec2': ['ec2', 'amazon ec2', 'elastic compute cloud', 'amazon elastic compute cloud - compute', 'amazon elastic compute cloud', 'ec2 - other'],
    's3': ['s3', 'amazon s3', 'simple storage service', 'amazon simple storage service'],
    'rds': ['rds', 'amazon rds', 'relational database service', 'amazon relational database service'],
    'apigateway': ['api gateway', 'amazon api gateway', 'apigateway'],
    'dynamodb': ['dynamodb', 'amazon dynamodb'],
    'stepfunctions': ['step functions', 'aws step functions', 'stepfunctions'],
    'guardduty': ['guardduty', 'amazon guardduty', 'aws guardduty'],
    'cloudwatch': ['cloudwatch', 'amazon cloudwatch', 'aws cloudwatch', 'amazoncloudwatch'],
    'ecs': ['ecs', 'amazon ecs', 'elastic container service', 'amazon elastic container service'],
    'vpc': ['vpc', 'amazon vpc', 'virtual private cloud', 'amazon virtual private cloud'],
    'elasticache': ['elasticache', 'amazon elasticache', 'elastic cache'],
    'elb': ['elb', 'elastic load balancing', 'amazon elastic load balancing', 'load balancer'],
    'waf': ['waf', 'aws waf', 'web application firewall'],
    // Additional analytics services
    'kinesis': ['kinesis', 'amazon kinesis', 'kinesis data streams'],
    'glue': ['glue', 'aws glue'],
    'kms': ['key management service', 'aws key management service', 'kms', 'aws kms', 'key management'],
    'securityhub': ['security hub', 'aws security hub', 'securityhub'],
    // Extended coverage (DMI cost-table services)
    'acm': ['certificate manager', 'aws certificate manager', 'acm'],
    'accessanalyzer': ['iam access analyzer', 'access analyzer', 'accessanalyzer'],
    'opensearch': ['opensearch service', 'amazon opensearch service', 'opensearch', 'amazon opensearch'],
    'inspector': ['inspector', 'amazon inspector'],
    'transfer': ['transfer family', 'aws transfer family', 'transfer'],
    'appflow': ['appflow', 'amazon appflow'],
    'dms': ['dms', 'database migration service', 'aws database migration service'],
    // MWAA (Managed Workflows for Apache Airflow)
    'mwaa': ['mwaa', 'amazon mwaa', 'managed workflows', 'apache airflow', 'airflow'],
    // Operations & Compliance
    'backup': ['backup', 'aws backup'],
    'config': ['config', 'aws config'],
    'codepipeline': ['codepipeline', 'aws codepipeline', 'code pipeline'],
    'msk': ['msk', 'amazon msk', 'kafka', 'managed streaming'],
  };

  // Get cost for each service by checking various name variations
  // AGGREGATES all matching costs (e.g., EC2 Compute + EC2 Other)
  const getServiceCost = (serviceKey) => {
    const variations = serviceMapping[serviceKey] || [serviceKey];
    let totalCost = 0;
    const matchedServices = new Set(); // Track already matched services to avoid double counting
    
    for (const variation of variations) {
      for (const [costServiceName, cost] of Object.entries(serviceCostMap)) {
        // Skip if already counted this service
        if (matchedServices.has(costServiceName)) continue;
        
        if (costServiceName.includes(variation) || variation.includes(costServiceName)) {
          totalCost += cost;
          matchedServices.add(costServiceName);
        }
      }
    }
    return totalCost;
  };

  // Check if a service has actual usage/data (any meaningful data exists)
  // Show section if there's cost > 0 OR ANY non-zero value in the data
  const hasServiceData = (serviceKey) => {
    const cost = getServiceCost(serviceKey);
    
    // If service has cost > 0, it's being used - show it
    if (cost > 0) return true;
    
    // Check if ANY data exists for this service
    const data = accountData[serviceKey];
    if (!data) return false;

    // Universal check: if ANY numeric value in the data object is > 0, show the section
    // This handles all services consistently
    const hasAnyData = (obj) => {
      for (const key in obj) {
        const value = obj[key];
        // Check numeric values
        if (typeof value === 'number' && value > 0) return true;
        // Check arrays with data
        if (Array.isArray(value) && value.length > 0) return true;
        // Check nested objects (but skip deep nesting)
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          if (hasAnyData(value)) return true;
        }
      }
      return false;
    };

    return hasAnyData(data);
  };

  // Create ordered list of services based on cost - show all services
  const serviceOrder = [
    { key: 'lambda', cost: getServiceCost('lambda'), hasData: hasServiceData('lambda') },
    { key: 'ec2', cost: getServiceCost('ec2'), hasData: hasServiceData('ec2') },
    { key: 's3', cost: getServiceCost('s3'), hasData: hasServiceData('s3') },
    { key: 'rds', cost: getServiceCost('rds'), hasData: hasServiceData('rds') },
    { key: 'dynamodb', cost: getServiceCost('dynamodb'), hasData: hasServiceData('dynamodb') },
    { key: 'ecs', cost: getServiceCost('ecs'), hasData: hasServiceData('ecs') },
    { key: 'apigateway', cost: getServiceCost('apigateway'), hasData: hasServiceData('apigateway') },
    { key: 'stepfunctions', cost: getServiceCost('stepfunctions'), hasData: hasServiceData('stepfunctions') },
    { key: 'vpc', cost: getServiceCost('vpc'), hasData: hasServiceData('vpc') },
    { key: 'elasticache', cost: getServiceCost('elasticache'), hasData: hasServiceData('elasticache') },
    { key: 'elb', cost: getServiceCost('elb'), hasData: hasServiceData('elb') },
    { key: 'waf', cost: getServiceCost('waf'), hasData: hasServiceData('waf') },
    { key: 'cloudwatch', cost: getServiceCost('cloudwatch'), hasData: hasServiceData('cloudwatch') },
    { key: 'guardduty', cost: getServiceCost('guardduty'), hasData: hasServiceData('guardduty') },
    // New monitoring services
    { key: 'sns', cost: getServiceCost('sns'), hasData: hasServiceData('sns') },
    { key: 'sqs', cost: getServiceCost('sqs'), hasData: hasServiceData('sqs') },
    { key: 'cloudfront', cost: getServiceCost('cloudfront'), hasData: hasServiceData('cloudfront') },
    { key: 'secretsmanager', cost: getServiceCost('secretsmanager'), hasData: hasServiceData('secretsmanager') },
    { key: 'parameterstore', cost: getServiceCost('parameterstore'), hasData: hasServiceData('parameterstore') },
    { key: 'ecr', cost: getServiceCost('ecr'), hasData: hasServiceData('ecr') },
    { key: 'eventbridge', cost: getServiceCost('eventbridge'), hasData: hasServiceData('eventbridge') },
    // Analytics services
    { key: 'kinesis', cost: getServiceCost('kinesis'), hasData: hasServiceData('kinesis') },
    { key: 'glue', cost: getServiceCost('glue'), hasData: hasServiceData('glue') },
    // MWAA (Apache Airflow)
    { key: 'mwaa', cost: getServiceCost('mwaa'), hasData: hasServiceData('mwaa') },
    // Operations & Compliance
    { key: 'backup', cost: getServiceCost('backup'), hasData: hasServiceData('backup') },
    { key: 'config', cost: getServiceCost('config'), hasData: hasServiceData('config') },
    { key: 'codepipeline', cost: getServiceCost('codepipeline'), hasData: hasServiceData('codepipeline') },
    { key: 'msk', cost: getServiceCost('msk'), hasData: hasServiceData('msk') },
    // Extended coverage (DMI cost-table services)
    { key: 'acm', cost: getServiceCost('acm'), hasData: hasServiceData('acm') },
    { key: 'accessanalyzer', cost: getServiceCost('accessanalyzer'), hasData: hasServiceData('accessanalyzer') },
    { key: 'kms', cost: getServiceCost('kms'), hasData: hasServiceData('kms') },
    { key: 'opensearch', cost: getServiceCost('opensearch'), hasData: hasServiceData('opensearch') },
    { key: 'inspector', cost: getServiceCost('inspector'), hasData: hasServiceData('inspector') },
    { key: 'transfer', cost: getServiceCost('transfer'), hasData: hasServiceData('transfer') },
    { key: 'appflow', cost: getServiceCost('appflow'), hasData: hasServiceData('appflow') },
    { key: 'securityhub', cost: getServiceCost('securityhub'), hasData: hasServiceData('securityhub') },
    { key: 'dms', cost: getServiceCost('dms'), hasData: hasServiceData('dms') },
  ]
  .sort((a, b) => b.cost - a.cost); // Sort by cost descending

  // A service is "unsampled" when the backend returned the resource inventory
  // count but could not sample the expensive CloudWatch metrics (sampling
  // timed out / was throttled). FEAT-001 signals this with metricsSampled===false
  // and leaves the affected metric fields as null. We must NOT render such a
  // service as green "Healthy" just because `(field || 0)` coalesces null to 0;
  // instead show a neutral "Partial data" badge while still showing the count.
  // `fields` lets a case flag unknown when its specific health inputs are null
  // even if metricsSampled is absent.
  const isUnsampled = (data, fields) => {
    if (!data) return true;
    if (data.metricsSampled === false) return true;
    if (fields && fields.length) {
      return fields.every((f) => data[f] == null);
    }
    return false;
  };

  const UNKNOWN_HEALTH = { status: 'unknown', icon: '⚪', label: 'Partial data' };

  // Calculate service health status
  const getServiceHealth = (serviceKey) => {
    const data = accountData[serviceKey];
    // The CloudWatch tile reflects alarm health only; its badge must not fall
    // back to "Partial data" just because the cloudwatch counts object is absent.
    if (!data && serviceKey !== 'cloudwatch') return UNKNOWN_HEALTH;

    switch(serviceKey) {
      case 'lambda':
        if (isUnsampled(data, ['errorRate', 'throttles'])) return UNKNOWN_HEALTH;
        if ((data.errorRate || 0) > 5 || (data.throttles || 0) > 100) return { status: 'critical', icon: '🔴', label: 'Critical' };
        if ((data.errorRate || 0) > 2 || (data.throttles || 0) > 50) return { status: 'warning', icon: '🟡', label: 'Warning' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };
      
      case 'ec2':
        if (isUnsampled(data, ['avgCpu'])) return UNKNOWN_HEALTH;
        const totalInstances = (data.running || 0) + (data.stopped || 0);
        if (totalInstances > 0 && (data.avgCpu || 0) > 90) return { status: 'critical', icon: '🔴', label: 'Critical' };
        if (totalInstances > 0 && (data.avgCpu || 0) > 75) return { status: 'warning', icon: '🟡', label: 'Warning' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };
      
      case 'rds':
        if (isUnsampled(data, ['cpuUtil'])) return UNKNOWN_HEALTH;
        if ((data.cpuUtil || 0) > 90) return { status: 'critical', icon: '🔴', label: 'Critical' };
        if ((data.cpuUtil || 0) > 75) return { status: 'warning', icon: '🟡', label: 'Warning' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };
      
      case 'apigateway':
        if (isUnsampled(data, ['requests', 'errors4xx', 'errors5xx'])) return UNKNOWN_HEALTH;
        const totalRequests = data.requests || 0;
        const totalErrors = (data.errors4xx || 0) + (data.errors5xx || 0);
        const errorRate = totalRequests > 0 ? (totalErrors / totalRequests) * 100 : 0;
        if (errorRate > 10) return { status: 'critical', icon: '🔴', label: 'Critical' };
        if (errorRate > 5) return { status: 'warning', icon: '🟡', label: 'Warning' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };
      
      case 'stepfunctions':
        if (isUnsampled(data, ['executions', 'successRate'])) return UNKNOWN_HEALTH;
        if ((data.successRate || 100) < 90 && (data.executions || 0) > 0) return { status: 'critical', icon: '🔴', label: 'Critical' };
        if ((data.successRate || 100) < 95 && (data.executions || 0) > 0) return { status: 'warning', icon: '🟡', label: 'Warning' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };
      
      case 'guardduty':
        if ((data.highSeverity || 0) > 0) return { status: 'critical', icon: '🔴', label: 'Critical' };
        if ((data.mediumSeverity || 0) > 5) return { status: 'warning', icon: '🟡', label: 'Warning' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };
      
      case 'cloudwatch':
        // Check alarms in ALARM state
        const alarmsInAlarm = accountData.alarms?.alarm || 0;
        if (alarmsInAlarm > 5) return { status: 'critical', icon: '🔴', label: 'Critical' };
        if (alarmsInAlarm > 0) return { status: 'warning', icon: '🟡', label: 'Warning' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };

      case 'backup':
        if ((data.failedJobs24h || 0) > 0) return { status: 'warning', icon: '🟡', label: 'Failed Jobs' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };

      case 'config':
        if ((data.nonCompliantRules || 0) > 5) return { status: 'critical', icon: '🔴', label: 'Non-Compliant' };
        if ((data.nonCompliantRules || 0) > 0) return { status: 'warning', icon: '🟡', label: 'Issues' };
        return { status: 'healthy', icon: '🟢', label: 'Compliant' };

      case 'codepipeline':
        if ((data.failed || 0) > 0) return { status: 'warning', icon: '🟡', label: 'Failed' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };

      case 'msk':
        if ((data.activeClusters || 0) < (data.clusters || 0)) return { status: 'warning', icon: '🟡', label: 'Issues' };
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };
      
      default:
        return { status: 'healthy', icon: '🟢', label: 'Healthy' };
    }
  };

  // Builds a SummaryTile for a service. Its numeric metric set is resolved from
  // the shared serviceStats descriptors and its chart body from serviceCharts,
  // both passed the same data slice. The card shows the service's complete stat
  // set in a dense mini-grid plus its chart(s) inline, with each defined exactly
  // once in its shared module. Null fields render the em-dash placeholder (never
  // a fabricated 0).
  const makeTile = (key) => (
    <SummaryTile
      key={key}
      className="summary-tile"
      serviceKey={key}
      displayName={serviceDisplayName(key)}
      cost={getServiceCost(key === 'eventbridge' ? 'events' : key)}
      stats={getServiceStats(key, accountData[key] || {}, accountData)}
      charts={getServiceCharts(key, accountData[key] || {}, accountData)}
    />
  );

  // Full-width status cards. One entry per service key (same keys as before),
  // each resolving its numeric metrics from serviceStats and its chart(s) from
  // serviceCharts. getServiceHealth / getServiceCost are unchanged.
  const serviceSections = {
    lambda: makeTile('lambda'),
    ec2: makeTile('ec2'),
    s3: makeTile('s3'),
    rds: makeTile('rds'),
    apigateway: makeTile('apigateway'),
    dynamodb: makeTile('dynamodb'),
    ecs: makeTile('ecs'),
    stepfunctions: makeTile('stepfunctions'),
    cloudwatch: makeTile('cloudwatch'),
    guardduty: makeTile('guardduty'),
    vpc: makeTile('vpc'),
    elasticache: makeTile('elasticache'),
    elb: makeTile('elb'),
    waf: makeTile('waf'),
    sns: makeTile('sns'),
    sqs: makeTile('sqs'),
    cloudfront: makeTile('cloudfront'),
    secretsmanager: makeTile('secretsmanager'),
    parameterstore: makeTile('parameterstore'),
    ecr: makeTile('ecr'),
    eventbridge: makeTile('eventbridge'),
    kinesis: makeTile('kinesis'),
    glue: makeTile('glue'),
    mwaa: makeTile('mwaa'),
    backup: makeTile('backup'),
    config: makeTile('config'),
    codepipeline: makeTile('codepipeline'),
    msk: makeTile('msk'),
    acm: makeTile('acm'),
    accessanalyzer: makeTile('accessanalyzer'),
    kms: makeTile('kms'),
    opensearch: makeTile('opensearch'),
    inspector: makeTile('inspector'),
    transfer: makeTile('transfer'),
    appflow: makeTile('appflow'),
    securityhub: makeTile('securityhub'),
    dms: makeTile('dms'),
  };

  // Format a USD monthly savings amount as "$1,234.56/month".
  const formatMonthly = (amount) =>
    `$${Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}/month`;

  // Quick-nav items (shared by the header nav and the floating sticky nav).
  // Order mirrors the sections down the page; conditional pills are included
  // only when their section actually renders.
  const quickNavItems = [
    { id: 'sec-cost', label: '💰 Cost' },
    { id: 'sec-issues', label: '🚨 Potential Issues' },
    { id: 'sec-idle', label: '🧹 Idle Resources' },
    { id: 'sec-top-alarms', label: '🔔 Top 10 Alarms' },
    { id: 'sec-predictive', label: '🔮 Predictive' },
    { id: 'sec-kpis', label: '📐 KPIs' },
    ...(isJiraEnabled(toInternalTeamName(teamName), environment)
      ? [{ id: 'sec-jira', label: '🎫 Zafran JIRA' }] : []),
    ...(accountData && accountData.healthScore
      ? [{ id: 'sec-health', label: '📊 Health' }] : []),
    { id: 'sec-alarms', label: '🚨 Alarms' },
    { id: 'sec-infra', label: '🔧 Infrastructure' },
    ...(hasTeamIntegrations(toInternalTeamName(teamName), environment)
      ? [{ id: 'sec-ies', label: '🔗 Integrations' }] : []),
  ];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="account-detail">
      {/* Floating quick-nav: pinned to the viewport (position: fixed) so it is
          always reachable while scrolling, regardless of overflow ancestors
          that break position: sticky. Shown once scrolled past the header. */}
      {showFloatingNav && (
        <nav className="account-floating-nav" aria-label="Jump to section">
          {quickNavItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="account-quicknav-btn"
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      )}
      {loading && accountData && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-text">Refreshing data...</div>
          <div className="loading-subtext">Fetching latest metrics</div>
        </div>
      )}
      
      {/* Sticky account header: stays visible while scrolling, and carries a
          quick-jump nav so users can jump straight to a feature section. */}
      <div className="account-header account-header--sticky">
        <div className="account-header-main">
          <div className="account-breadcrumb">
            <span className="breadcrumb-item">Teams</span>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item team-name">{teamName}</span>
            <span className="breadcrumb-separator">›</span>
            <span className={`breadcrumb-item env-badge-header env-${environment}`}>{environment.toUpperCase()}</span>
          </div>
          <h2>{teamName} - {environment.toUpperCase()} Environment</h2>
          <p>Account ID: {currentAccountId}</p>
        </div>

        {/* Quick-jump navigation - click to scroll to a feature section. */}
        <nav className="account-quicknav" aria-label="Jump to section">
          {quickNavItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="account-quicknav-btn"
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Region Selector - Prominent Section (top of page) */}
      <section className="section-container region-selector-section">
        <div className="region-selector-prominent">
          <div className="region-selector-content">
            <div className="region-selector-info">
              <h3 className="region-selector-title">🌍 Select AWS Region</h3>
              <p className="region-selector-description">
                The sections below (CloudWatch Alarms, Cost Recommendations, and Infrastructure Monitoring) 
                are region-specific. Select the region where your resources are deployed.
              </p>
            </div>
            <div className="region-selector-control">
              <select 
                id="region-select-main"
                value={selectedRegion} 
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="region-select-large"
              >
                {availableRegions.map(region => (
                  <option key={region.value} value={region.value}>
                    {region.label}
                  </option>
                ))}
              </select>
              <span className="region-default-hint">
                Default for {teamName}: {availableRegions.find(r => r.value === getDefaultRegion(teamName))?.label || 'Frankfurt'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Potential Issues — surfaced at the TOP so critical problems (alarms in
          ALARM, unhealthy/high-CPU EC2, low-storage RDS, high-severity GuardDuty,
          SQS backlog) are the first thing seen. Each line is guarded; "No critical
          issues detected" shows only when none apply. */}
      <section id="sec-issues" className="section-container" aria-label="Potential issues">
        <div className="insight-card insight-issues">
          <h4>🚨 Potential Issues</h4>
          <ul className="insight-list">
            {(accountData.alarms?.alarm || 0) > 0 && (
              <li className="insight-critical">
                <span className="insight-icon">⚠️</span>
                <span>{accountData.alarms.alarm} CloudWatch alarm(s) in ALARM state</span>
              </li>
            )}
            {(accountData.ec2?.unhealthyInstances || 0) > 0 && (
              <li className="insight-critical">
                <span className="insight-icon">🔴</span>
                <span>{accountData.ec2.unhealthyInstances} EC2 instance(s) unhealthy</span>
              </li>
            )}
            {(accountData.ec2?.highCpuInstances || 0) > 0 && (
              <li className="insight-warning">
                <span className="insight-icon">🟡</span>
                <span>{accountData.ec2.highCpuInstances} EC2 instance(s) with high CPU (&gt;80%)</span>
              </li>
            )}
            {(accountData.rds?.lowStorageInstances || 0) > 0 && (
              <li className="insight-critical">
                <span className="insight-icon">💾</span>
                <span>{accountData.rds.lowStorageInstances} RDS instance(s) with low storage (&lt;20%)</span>
              </li>
            )}
            {(accountData.rds?.replicaLag || 0) > 60 && (
              <li className="insight-warning">
                <span className="insight-icon">⏱️</span>
                <span>RDS replica lag: {accountData.rds.replicaLag}s (consider investigating)</span>
              </li>
            )}
            {(accountData.guardduty?.highSeverity || 0) > 0 && (
              <li className="insight-critical">
                <span className="insight-icon">🛡️</span>
                <span>{accountData.guardduty.highSeverity} high severity GuardDuty finding(s)</span>
              </li>
            )}
            {(accountData.sqs?.messagesVisible || 0) > 10000 && (
              <li className="insight-warning">
                <span className="insight-icon">📨</span>
                <span>SQS: {accountData.sqs.messagesVisible.toLocaleString()} messages in queues (possible backlog)</span>
              </li>
            )}
            {(accountData.alarms?.alarm || 0) === 0 &&
             (accountData.ec2?.unhealthyInstances || 0) === 0 &&
             (accountData.ec2?.highCpuInstances || 0) === 0 &&
             (accountData.rds?.lowStorageInstances || 0) === 0 &&
             (accountData.guardduty?.highSeverity || 0) === 0 && (
              <li className="insight-ok">
                <span className="insight-icon">✅</span>
                <span>No critical issues detected</span>
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* Cost Analysis Section */}
      <section id="sec-cost" className="section-container" aria-label="Cost analysis">
        <h3 className="section-title">
          💰 Cost Analysis
          <span className="info-icon-wrapper">
            <span 
              className="info-icon"
              title="Cost data is cached and updated periodically. Infrastructure metrics are real-time."
            >
              ℹ️
            </span>
            <span className="info-tooltip">
              <strong>About Cost Data:</strong><br/>
              {environment.toLowerCase() === 'dev' ? (
                <>
                  Cost information in this section is cached and updated <strong>monthly on the 1st at 1 AM UTC</strong> in the Development environment.
                </>
              ) : (
                <>
                  Cost information in this section is cached and updated <strong>every 3 days at 1 AM UTC</strong> in the Production environment.
                </>
              )}
              <br/><br/>
              <strong>Note:</strong> Infrastructure metrics (EC2, Lambda, RDS, etc.) below are fetched in real-time when you view this page. Cost recommendations are collected daily.
            </span>
          </span>
        </h3>
        
        {/* Cost Data Freshness Indicator */}
        {accountData.costDataFreshness && (
          <div className="cost-data-freshness-banner">
            <div className="freshness-content">
              <div className="freshness-icon">💰</div>
              <div className="freshness-info">
                <div className="freshness-main">
                  <strong>Cost Data Last Updated:</strong> {new Date(accountData.costDataFreshness.lastUpdated).toLocaleString()}
                  <span className="freshness-age">({accountData.costDataFreshness.ageHours}h ago)</span>
                </div>
                <div className="freshness-next">
                  <strong>Next Cost Update:</strong> {new Date(accountData.costDataFreshness.nextUpdate).toLocaleString()}
                  {accountData.costDataFreshness.updateFrequency === 'monthly' && (
                    <span className="freshness-schedule"> (Monthly on 1st)</span>
                  )}
                  {accountData.costDataFreshness.updateFrequency === 'every-3-days' && (
                    <span className="freshness-schedule"> (Every 3 days)</span>
                  )}
                </div>
                <div className="freshness-note">
                  <em>Note: Infrastructure metrics (EC2, Lambda, etc.) are fetched in real-time. Only cost data is cached.</em>
                </div>
              </div>
              <div className="freshness-env">
                <span className={`env-badge env-${accountData.costDataFreshness.environment}`}>
                  {accountData.costDataFreshness.environment.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Cost KPI summary cards. Each card is pushed ONLY when its source
            value is a finite number, so an absent figure is omitted rather than
            shown as $0. Values are read from accountData / optimizationModel —
            no new backend fields. */}
        {(() => {
          const fmtUsd = (v) =>
            `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
          const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

          const mtd = accountData.currentCost ?? accountData.totalCost?.current;
          const lastMonth = accountData.lastMonth ?? accountData.totalCost?.lastMonth;
          const forecast = accountData.forecast ?? accountData.totalCost?.forecast;
          const forecastChange = accountData.totalCost?.forecastChange;
          const savings = optimizationModel.combinedSavings;

          const cards = [];
          if (isNum(mtd)) {
            // Month-to-date is a PARTIAL period, so a "% vs last month" against
            // the FULL previous month is apples-to-oranges (MTD is naturally
            // lower mid-month and would read as a false decrease). The honest
            // trend signal lives on the Forecast (EOM) card, which compares a
            // full projected month to the full last month. Show MTD as a plain
            // month-to-date figure with a neutral sublabel, no misleading delta.
            cards.push({ label: 'This month (MTD)', value: fmtUsd(mtd), hint: 'month to date' });
          }
          if (isNum(lastMonth)) {
            cards.push({ label: 'Last month', value: fmtUsd(lastMonth) });
          }
          if (isNum(forecast)) {
            const card = { label: 'Forecast (EOM)', value: fmtUsd(forecast) };
            if (isNum(forecastChange)) {
              card.delta = `${forecastChange >= 0 ? '+' : ''}${forecastChange}%`;
              card.deltaDirection = forecastChange >= 0 ? 'up' : 'down';
            }
            cards.push(card);
          }
          if (isNum(savings) && savings > 0) {
            cards.push({
              label: 'Potential savings',
              value: fmtUsd(savings),
              hint: 'per month',
              valueTone: 'positive',
            });
          }
          return <CostKpiRow cards={cards} />;
        })()}

        {/* Compact Datadog-style density grid for the cost blocks. Pure layout:
            each child keeps its own render guard and data bindings. */}
        <div className="acct-cost-grid">

        {/* Top row (2-up): Cost Trend (Last 30 Days) and By Environment side by
            side. Each cell keeps its own render guard. */}
        <div className="acct-cost-row-2up cost-row-top">

        {/* Cost Trend (Last 30 Days). Guard and internals unchanged. */}
        {(() => {
          const trend = accountData.costTrend || [];
          const costs = trend.map(d => Number(d.cost) || 0);
          const total = costs.reduce((a, b) => a + b, 0);
          const avg = costs.length ? total / costs.length : 0;
          const max = costs.length ? Math.max(...costs) : 0;
          const peakDay = trend.find(d => (Number(d.cost) || 0) === max);
          const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
          const fmt2 = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

          const CostTooltip = ({ active, payload, label }) => {
            if (!active || !payload || !payload.length) return null;
            const val = payload[0].value;
            const vsAvg = avg ? ((val - avg) / avg) * 100 : 0;
            return (
              <div className="cost-trend-tooltip">
                <div className="ctt-date">{label}</div>
                <div className="ctt-value">{fmt2(val)}</div>
                <div className={`ctt-delta ${vsAvg >= 0 ? 'up' : 'down'}`}>
                  {vsAvg >= 0 ? '▲' : '▼'} {Math.abs(vsAvg).toFixed(0)}% vs avg
                </div>
              </div>
            );
          };

          return (
            <div className="chart-card chart-card-full cost-trend-card">
              <div className="cost-trend-header">
                <h4>Cost Trend (Last 30 Days)</h4>
                <div className="cost-trend-stats">
                  <div className="cts-item">
                    <span className="cts-label">Total (30d)</span>
                    <span className="cts-value">{fmt(total)}</span>
                  </div>
                  <div className="cts-item">
                    <span className="cts-label">Daily avg</span>
                    <span className="cts-value">{fmt(avg)}</span>
                  </div>
                  <div className="cts-item">
                    <span className="cts-label">Peak</span>
                    <span className="cts-value">{fmt(max)}</span>
                    {peakDay && <span className="cts-sub">{peakDay.date}</span>}
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trend} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
                  <defs>
                    {/* Richer multi-stop fill: KONE blue fading through teal for a
                        more attractive, modern look. */}
                    <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0051A3" stopOpacity={0.45}/>
                      <stop offset="45%" stopColor="#1E88E5" stopOpacity={0.25}/>
                      <stop offset="80%" stopColor="#00A3A3" stopOpacity={0.10}/>
                      <stop offset="100%" stopColor="#00A3A3" stopOpacity={0}/>
                    </linearGradient>
                    {/* Gradient stroke so the line itself shifts colour left-to-right. */}
                    <linearGradient id="costStroke" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#0051A3"/>
                      <stop offset="60%" stopColor="#1E88E5"/>
                      <stop offset="100%" stopColor="#00A3A3"/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e6ecf2" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#8a97a8"
                    tick={{ fontSize: 12 }}
                    tickMargin={10}
                    minTickGap={20}
                    angle={-30}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis
                    stroke="#8a97a8"
                    tick={{ fontSize: 12 }}
                    width={70}
                    tickFormatter={fmt}
                  />
                  <Tooltip content={<CostTooltip />} />
                  {avg > 0 && (
                    <ReferenceLine
                      y={avg}
                      stroke="#F9A825"
                      strokeDasharray="6 4"
                      label={{ value: `avg ${fmt(avg)}`, position: 'right', fill: '#F9A825', fontSize: 11 }}
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="cost"
                    name="Daily cost"
                    stroke="url(#costStroke)"
                    fillOpacity={1}
                    fill="url(#costGradient)"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: '#ffffff', fill: '#00A3A3' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* By environment — LAST / CURRENT / FORECAST per environment as a
            grouped bar chart, built from the already-fetched teamCostRows. Env
            names are derived from the data; only finite values are plotted (a
            missing value simply has no bar, never a fabricated 0). Wrapped in the
            same chart-card as the two trend panels so the 3-up row aligns. */}
        {teamCostRows.length > 0 && (() => {
          const fmt0 = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
          const labelFmt = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? fmt0(v) : '');
          const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
          const envData = teamCostRows.map((row) => ({
            env: row.environment,
            lastMonth: num(row.lastMonth),
            currentMonth: num(row.currentMonth),
            forecast: num(row.forecast),
          }));
          return (
            <div className="chart-card chart-card-full cost-env-card">
              <div className="chart-card-header">
                <h4>By Environment</h4>
                <span className="chart-card-subtitle">Last · current · forecast per environment</span>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={envData} margin={{ top: 16, right: 16, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="env" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={fmt0} width={64} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => fmt0(value)} />
                  <Legend />
                  <Bar dataKey="lastMonth" name="Last month" fill="#90A4AE" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="lastMonth" position="top" formatter={labelFmt} style={{ fontSize: 10, fill: '#374151' }} />
                  </Bar>
                  <Bar dataKey="currentMonth" name="Current month" fill="#0051A3" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="currentMonth" position="top" formatter={labelFmt} style={{ fontSize: 10, fill: '#374151' }} />
                  </Bar>
                  <Bar dataKey="forecast" name="Forecast" fill="#F9A825" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="forecast" position="top" formatter={labelFmt} style={{ fontSize: 10, fill: '#374151' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        </div>
        {/* End top row */}

        {/* Bottom row (2-up): the top-10 Cost-by-service table (wide) beside the
            Optimization Opportunities panel (wide). Each cell keeps its own
            guard. */}
        <div className="acct-cost-row-2up cost-row-service-opt">

        {/* Top-10 Cost-by-service chart: a horizontal grouped bar chart with
            current- and last-month cost per service. Per-service forecast is
            intentionally omitted — the account collector does not emit one, so
            the real forecast lives on the Forecast KPI card and the
            By-Environment chart. */}
        {accountData.costBreakdown && accountData.costBreakdown.length > 0 && (
        <div className="chart-card chart-card-full cost-service-card">
          <div className="chart-card-header">
            <h4>Cost by Service</h4>
            <span className="chart-card-subtitle">Top 10 services by current-month cost</span>
          </div>

          {(() => {
            const breakdown = accountData.costBreakdown || [];
            const fmtUsd = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
            const labelFmt = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? fmtUsd(v) : '');
            const data = [...breakdown]
              .sort((a, b) => Number(b.current || 0) - Number(a.current || 0))
              .slice(0, 10)
              .map((item) => ({
                service: item.service,
                current: Number(item.current || 0),
                lastMonth: Number(item.lastMonth || 0),
              }));
            const chartHeight = Math.max(320, data.length * 40 + 60);
            return (
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={data} layout="vertical" margin={{ top: 8, right: 56, left: 8, bottom: 8 }} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={fmtUsd} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="service" width={210} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => fmtUsd(value)} />
                  <Legend />
                  <Bar dataKey="current" name="Current month" fill="#0051A3" radius={[0, 3, 3, 0]}>
                    <LabelList dataKey="current" position="right" formatter={labelFmt} style={{ fontSize: 10, fill: '#374151' }} />
                  </Bar>
                  <Bar dataKey="lastMonth" name="Last month" fill="#90A4AE" radius={[0, 3, 3, 0]}>
                    <LabelList dataKey="lastMonth" position="right" formatter={labelFmt} style={{ fontSize: 10, fill: '#374151' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            );
          })()}
        </div>
        )}

        {/* Optimization Opportunities — the existing optimizationModel panel.
            Savings math is reused verbatim (not recomputed). */}
        <div className="insight-card insight-optimize">
          <h4>💡 Optimization Opportunities</h4>

          {/* Combined potential monthly savings = AWS Cost Explorer (authoritative)
              + the capped metrics-based Estimate recs. This headline total must
              match the sum of everything listed below it. We show a single
              combined figure, then break it down by source underneath so AWS and
              Estimate contributions remain distinguishable. */}
          {/* Headline = the LARGER of the two independent sources, never their
              sum. Trusted Advisor's cost checks overlap Cost Explorer's, so
              adding them would double count; each is listed separately below so
              the figure stays traceable and comparable to the AWS console. */}
          {(optimizationModel.combinedSavings > 0 || optimizationModel.taSavingsTotal > 0) && (() => {
            const ce = optimizationModel.combinedSavings;
            const ta = optimizationModel.taSavingsTotal;
            const headline = Math.max(ce, ta);
            const taLeads = ta > ce;
            return (
              <div className="savings-aggregate">
                <span className="savings-aggregate-label">Potential monthly savings</span>
                <span className="savings-aggregate-value">{formatMonthly(headline)}</span>
                {taLeads ? (
                  <span className="savings-source-label source-aws">AWS Trusted Advisor</span>
                ) : optimizationModel.aggregateSavings > 0 && optimizationModel.rightsizingEstimateTotal > 0 ? (
                  <span className="savings-source-label source-mixed">AWS + Estimate</span>
                ) : optimizationModel.aggregateSavings > 0 ? (
                  <span className="savings-source-label source-aws">AWS Cost Explorer</span>
                ) : (
                  <span className="savings-source-label source-estimate">Estimate</span>
                )}
                {awsRecommendationsStale && optimizationModel.aggregateSavings > 0 && (
                  <span className="savings-stale-note">⚠️ data may be stale (&gt;7 days old)</span>
                )}
              </div>
            );
          })()}

          {/* Per-source breakdown. Trusted Advisor is shown as its own row
              whenever it reported savings, so the gap against the AWS console's
              Cost Optimization total is visible rather than unexplained. */}
          {(optimizationModel.taSavingsTotal > 0
            || (optimizationModel.aggregateSavings > 0 && optimizationModel.rightsizingEstimateTotal > 0)) && (
            <div className="savings-breakdown">
              {optimizationModel.aggregateSavings > 0 && (
                <span className="savings-breakdown-item">
                  AWS Cost Explorer: <strong>{formatMonthly(optimizationModel.aggregateSavings)}</strong>
                </span>
              )}
              {optimizationModel.rightsizingEstimateTotal > 0 && (
                <span className="savings-breakdown-item">
                  Estimate: <strong>{formatMonthly(optimizationModel.rightsizingEstimateTotal)}</strong>
                </span>
              )}
              {optimizationModel.otherRecsTotal > 0 && (
                <span className="savings-breakdown-item">
                  Other: <strong>{formatMonthly(optimizationModel.otherRecsTotal)}</strong>
                </span>
              )}
              {optimizationModel.taSavingsTotal > 0 && (
                <span className="savings-breakdown-item">
                  AWS Trusted Advisor: <strong>{formatMonthly(optimizationModel.taSavingsTotal)}</strong>
                  {' '}<span className="savings-source-note">(separate source, overlaps the above)</span>
                </span>
              )}
            </div>
          )}

          {/* Savings data unavailable indicator (Req 4.5 / 7.4) */}
          {optimizationModel.recsPayloadError && (
            <div className="savings-unavailable-note">
              ⚠️ AWS savings data is currently unavailable. Showing count-based hints only.
            </div>
          )}

          <ul className="insight-list">
            {/* Real AWS Cost Explorer recommendations, sorted by savings desc */}
            {optimizationModel.awsRecs.map((rec, index) => (
              <li key={`aws-${index}`} className="insight-info insight-savings">
                <span className="insight-icon">💰</span>
                <span className="insight-savings-body">
                  <span className="insight-savings-title">{rec.title}</span>
                  {rec.description && (
                    <span className="insight-savings-desc">{rec.description}</span>
                  )}
                  <span className="insight-savings-figure">
                    <span className="savings-amount">{formatMonthly(rec.savings)}</span>
                    <span className="savings-source-label source-aws">AWS Cost Explorer</span>
                  </span>
                </span>
              </li>
            ))}

            {/* AWS Trusted Advisor cost checks. Listed as their own source with
                their own figures, not merged into the items above: Trusted
                Advisor covers opportunities Cost Explorer never reports (idle
                load balancers, unassociated Elastic IPs, unattached EBS volumes,
                ...) but its low-utilization-EC2 and commitment checks overlap
                the Cost Explorer recs, so the totals are kept separate. */}
            {optimizationModel.taRecs.map((rec, index) => (
              <li key={`ta-${index}`} className="insight-info insight-savings">
                <span className="insight-icon">🔎</span>
                <span className="insight-savings-body">
                  <span className="insight-savings-title">
                    {rec.title}
                    {rec.count > 0 && (
                      <span className="insight-region-tag"> · {rec.count} resource(s)</span>
                    )}
                  </span>
                  {rec.description && (
                    <span className="insight-savings-desc">{rec.description}</span>
                  )}
                  <span className="insight-savings-figure">
                    <span className="savings-amount">{formatMonthly(rec.potentialSavings)}</span>
                    <span className="savings-source-label source-aws">AWS Trusted Advisor</span>
                  </span>
                </span>
              </li>
            ))}

            {/* Concrete instance-type-selection recommendations (Estimate source).
                Shown only when AWS Cost Explorer has no authoritative rightsizing
                rec, so we never duplicate the same opportunity. */}
            {!optimizationModel.awsTypesPresent.has('ec2-rightsizing') &&
              optimizationModel.rightsizingEstimates.map((rec, index) => (
                <li key={`rightsize-${index}`} className="insight-info insight-savings">
                  <span className="insight-icon">🧮</span>
                  <span className="insight-savings-body">
                    <span className="insight-savings-title">
                      {rec.title}
                      {rec.region && rec.region !== 'global' && (
                        <span className="insight-region-tag"> · {rec.region}</span>
                      )}
                    </span>
                    {rec.description && (
                      <span className="insight-savings-desc">{rec.description}</span>
                    )}
                    <span className="insight-savings-figure">
                      <span className="savings-amount">{formatMonthly(rec.savings)}</span>
                      <span className="savings-source-label source-estimate">Estimate</span>
                    </span>
                  </span>
                </li>
              ))}

            {/* Other recommendations for this account (e.g. S3 "Storage
                Optimization" lifecycle policies) that are neither AWS Cost
                Explorer nor rightsizing. These appear on the overall dashboard
                and were previously dropped here; show them with their real
                monthly saving. */}
            {optimizationModel.otherRecs.map((rec, index) => (
              <li key={`other-${index}`} className="insight-info insight-savings">
                <span className="insight-icon">💡</span>
                <span className="insight-savings-body">
                  <span className="insight-savings-title">
                    {rec.title}
                    {rec.region && rec.region !== 'global' && (
                      <span className="insight-region-tag"> · {rec.region}</span>
                    )}
                  </span>
                  {rec.description && (
                    <span className="insight-savings-desc">{rec.description}</span>
                  )}
                  <span className="insight-savings-figure">
                    <span className="savings-amount">{formatMonthly(rec.savings)}</span>
                    {rec.source && (
                      <span className="savings-source-label source-aws">{rec.source}</span>
                    )}
                  </span>
                </span>
              </li>
            ))}

            {/* Stopped EC2 hint - only when AWS has no matching rec */}
            {optimizationModel.stoppedCount > 0 && (
              <li className="insight-info">
                <span className="insight-icon">🛑</span>
                <span className="insight-savings-body">
                  <span>{optimizationModel.stoppedCount} stopped EC2 instance(s) - consider terminating if unused</span>
                  <span className="insight-savings-figure">
                    {optimizationModel.stoppedEstimate != null ? (
                      <>
                        <span className="savings-amount">{formatMonthly(optimizationModel.stoppedEstimate)}</span>
                        <span className="savings-source-label source-estimate">Estimate</span>
                      </>
                    ) : (
                      <span className="savings-source-label source-nodata">No data</span>
                    )}
                  </span>
                </span>
              </li>
            )}

            {/* Low-CPU rightsizing hint - only when neither AWS Cost Explorer nor
                our metrics-based engine produced a concrete rightsizing rec. */}
            {optimizationModel.lowCpuCount > 0 &&
              !optimizationModel.awsTypesPresent.has('ec2-rightsizing') &&
              optimizationModel.rightsizingEstimates.length === 0 && (
              <li className="insight-info">
                <span className="insight-icon">📉</span>
                <span className="insight-savings-body">
                  <span>{optimizationModel.lowCpuCount} EC2 instance(s) with low CPU (&lt;10%) - consider rightsizing</span>
                  <span className="insight-savings-figure">
                    {optimizationModel.lowCpuEstimate != null ? (
                      <>
                        <span className="savings-amount">{formatMonthly(optimizationModel.lowCpuEstimate)}</span>
                        <span className="savings-source-label source-estimate">Estimate</span>
                      </>
                    ) : (
                      <span className="savings-source-label source-nodata">No data</span>
                    )}
                  </span>
                </span>
              </li>
            )}

            {/* Long-running / RI hint - only when AWS has no RI/SP rec */}
            {optimizationModel.oldCount > 0 && !optimizationModel.awsTypesPresent.has('reserved-instance') && (
              <li className="insight-info">
                <span className="insight-icon">📅</span>
                <span className="insight-savings-body">
                  <span>{optimizationModel.oldCount} EC2 instance(s) running &gt;90 days - review for Reserved Instances</span>
                  <span className="insight-savings-figure">
                    <span className="savings-source-label source-nodata">No data</span>
                  </span>
                </span>
              </li>
            )}

            {/* Non-cost hints (always applicable regardless of savings data) */}
            {(accountData.rds?.multiAZCount || 0) === 0 && (accountData.rds?.instances || 0) > 0 && (
              <li className="insight-info">
                <span className="insight-icon">🔄</span>
                <span>No Multi-AZ RDS instances - consider for production workloads</span>
              </li>
            )}
            {(accountData.secretsmanager?.rotationEnabled || 0) < (accountData.secretsmanager?.secrets || 0) &&
             (accountData.secretsmanager?.secrets || 0) > 0 && (
              <li className="insight-info">
                <span className="insight-icon">🔐</span>
                <span>
                  {(accountData.secretsmanager?.secrets || 0) - (accountData.secretsmanager?.rotationEnabled || 0)} secret(s) without rotation enabled
                </span>
              </li>
            )}

            {/* Nothing at all to show */}
            {optimizationModel.awsRecs.length === 0 &&
             optimizationModel.rightsizingEstimates.length === 0 &&
             optimizationModel.otherRecs.length === 0 &&
             optimizationModel.stoppedCount === 0 &&
             optimizationModel.lowCpuCount === 0 &&
             optimizationModel.oldCount === 0 &&
             !((accountData.rds?.multiAZCount || 0) === 0 && (accountData.rds?.instances || 0) > 0) &&
             !((accountData.secretsmanager?.rotationEnabled || 0) < (accountData.secretsmanager?.secrets || 0) &&
               (accountData.secretsmanager?.secrets || 0) > 0) && (
              <li className="insight-ok">
                <span className="insight-icon">✅</span>
                <span>No obvious optimization opportunities</span>
              </li>
            )}
          </ul>
        </div>

        </div>
        {/* End bottom row */}

        </div>
        {/* End .acct-cost-grid */}
      </section>

      {/* Zafran Vulnerability Findings (direct Zafran API) removed - we now
          use JIRA remediation tickets only. */}

      {/* Idle Resources - AWS resources unused for ~3 months. Placed above KPIs. */}
      <div id="sec-idle" className="quicknav-anchor">
        <IdleResourcesSection team={toInternalTeamName(teamName)} environment={environment} />
      </div>

      {/* Top 10 Most Frequently Triggered Alarms (Last 7 Days), with the
          Bedrock root-cause analysis of each. Placed right after Idle Resources. */}
      <section id="sec-top-alarms" className="section-container">
        <h3 className="section-title">🔔 Top 10 Alarms (Last 7 Days)</h3>
        <p className="section-description">
          Select an alarm to see its root cause, the failures found in its logs and the
          recommended fix.
        </p>
        <TopAlarmsSection topAlarms={topAlarms} />
      </section>

      {/* Predictive Monitoring - weekly AI (AWS Bedrock) predicted-failure list,
          derived from each account's real CloudWatch metrics + alarm activity.
          Placed above KPIs. */}
      <div id="sec-predictive" className="quicknav-anchor">
        <PredictiveMonitoringSection team={toInternalTeamName(teamName)} environment={environment} />
      </div>

      {/* KPIs / Non-Functional Requirements - shown for ALL teams. Performance,
          availability & scalability are all derived from the account's AWS
          CloudWatch metrics, collected daily (last 24 hours). */}
      <div id="sec-kpis" className="quicknav-anchor">
        <KpiSection team={toInternalTeamName(teamName)} environment={environment} />
      </div>

      {/* Zafran remediation tickets pulled from JIRA - only for teams that have
          a JIRA board configured (e.g. MyKONE). appId is the INTERNAL team name
          so it matches the S3 path jira/applications/{app}. */}
      {isJiraEnabled(toInternalTeamName(teamName), environment) && (
        <div id="sec-jira" className="quicknav-anchor">
          <JiraTicketsSection appId={toInternalTeamName(teamName)} />
        </div>
      )}

      {/* CloudWatch Alarms Section */}
      {/* Cost Optimization Recommendations are merged into the combined
          "Cost Optimization & Insights" section below (with Quick Insights). */}

      {/* Infrastructure Health Score */}
      {accountData.healthScore && (
        <section id="sec-health" className="section-container">
          <h3 className="section-title">
            📊 Infrastructure Health Score
            <span className="region-badge-inline">
              📍 {availableRegions.find(r => r.value === selectedRegion)?.label || selectedRegion}
            </span>
          </h3>
          <HealthScore healthData={accountData.healthScore} showDetails={true} />
        </section>
      )}

      {/* CloudWatch Alarms Section */}
      <section id="sec-alarms" className="section-container">
        <h3 className="section-title">🚨 CloudWatch Alarms</h3>
        <div className="alarms-grid">
          <div className="alarm-widget alarm-ok">
            <div className="alarm-count">{accountData.alarms?.ok || 0}</div>
            <div className="alarm-label">OK</div>
            <div className="alarm-sublabel">(includes INSUFFICIENT_DATA)</div>
          </div>
          <div 
            className={`alarm-widget alarm-critical ${(accountData.alarms?.alarm || 0) > 0 ? 'clickable' : ''}`}
            onClick={() => (accountData.alarms?.alarm || 0) > 0 && fetchAlarmDetails()}
            style={{ cursor: (accountData.alarms?.alarm || 0) > 0 ? 'pointer' : 'default' }}
          >
            <div className="alarm-count">{accountData.alarms?.alarm || 0}</div>
            <div className="alarm-label">ALARM</div>
            {(accountData.alarms?.alarm || 0) > 0 && (
              <div className="alarm-sublabel">Click to view details</div>
            )}
          </div>
        </div>
      </section>

      {/* Infrastructure Monitoring */}
      <section id="sec-infra" className="section-container">
        <div className="section-header-with-filters">
          <h3 className="section-title">
            🔧 Infrastructure Monitoring
            <span className="region-badge-inline">
              📍 {availableRegions.find(r => r.value === selectedRegion)?.label || selectedRegion}}
            </span>
          </h3>
          <div className="monitoring-filters">
            <div className="infra-search">
              <label htmlFor="infra-service-search" className="sr-only">Search services</label>
              <input
                id="infra-service-search"
                type="text"
                className="infra-search-input"
                placeholder="Search services…"
                value={infraSearch}
                onChange={(e) => setInfraSearch(e.target.value)}
                aria-label="Search services by name"
              />
            </div>
            <div className="infra-filter-toggle" role="group" aria-label="Filter services by status">
              <button
                type="button"
                className={`infra-toggle-btn ${infraFilter === 'all' ? 'active' : ''}`}
                aria-pressed={infraFilter === 'all'}
                onClick={() => setInfraFilter('all')}
              >
                All
              </button>
              <button
                type="button"
                className={`infra-toggle-btn ${infraFilter === 'issues' ? 'active' : ''}`}
                aria-pressed={infraFilter === 'issues'}
                onClick={() => setInfraFilter('issues')}
              >
                Issues only
              </button>
            </div>
            <div className="filter-info">
              <span className="info-label">Last Updated:</span>
              <span className="info-value">
                {!loadingInfra && lastUpdated ? lastUpdated.toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true 
                }) : 'Loading...'}
              </span>
            </div>
          </div>
        </div>
        
        {/* Infrastructure Loading - Full interactive loader. Shown only during
            the initial phase while NO batch has landed yet; once batches start
            arriving we switch to per-group rendering so each service group is
            revealed as its own batch completes. */}
        {loadingInfra && Object.values(infraBatchStatus).every(s => s === 'loading') ? (
          <div className="infra-loading-container">
            <div className="infra-loading-card">
              <div className="infra-loading-header">
                <div className="infra-loading-spinner-large"></div>
                <h4>Loading Infrastructure Metrics</h4>
                <p>Collecting real-time data from AWS CloudWatch (last 24 hours)</p>
              </div>
              
              <div className="infra-loading-progress">
                <div className="progress-bar">
                  <div className="progress-bar-fill"></div>
                </div>
                <span className="progress-text">Fetching metrics from multiple services...</span>
              </div>
              
              <div className="infra-loading-services-grid">
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="lambda" size={28} /></span>
                  <span className="service-name">Lambda</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="ec2" size={28} /></span>
                  <span className="service-name">EC2</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="rds" size={28} /></span>
                  <span className="service-name">RDS</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="s3" size={28} /></span>
                  <span className="service-name">S3</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="apigateway" size={28} /></span>
                  <span className="service-name">API Gateway</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="dynamodb" size={28} /></span>
                  <span className="service-name">DynamoDB</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="stepfunctions" size={28} /></span>
                  <span className="service-name">Step Functions</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="sns" size={28} /></span>
                  <span className="service-name">SNS/SQS</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="guardduty" size={28} /></span>
                  <span className="service-name">GuardDuty</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="cloudwatch" size={28} /></span>
                  <span className="service-name">CloudWatch</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="kinesis" size={28} /></span>
                  <span className="service-name">Kinesis</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="glue" size={28} /></span>
                  <span className="service-name">Glue</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="backup" size={28} /></span>
                  <span className="service-name">Backup</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="config" size={28} /></span>
                  <span className="service-name">Config</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="codepipeline" size={28} /></span>
                  <span className="service-name">CodePipeline</span>
                  <span className="service-status loading"></span>
                </div>
                <div className="loading-service-card">
                  <span className="service-icon"><AwsServiceIcon service="msk" size={28} /></span>
                  <span className="service-name">MSK</span>
                  <span className="service-status loading"></span>
                </div>
              </div>
              
              <div className="infra-loading-tip">
                <span className="tip-icon">💡</span>
                <span className="tip-text">This may take 15-30 seconds as we collect real-time metrics from AWS APIs</span>
              </div>
            </div>
          </div>
        ) : (
          /* Service groups - Only shown after loading completes */
          <>
            {(() => {
              const serviceGroups = {
                compute: {
                  title: '🖥️ Compute',
                  services: ['lambda', 'ec2', 'ecs', 'stepfunctions']
                },
                storage: {
                  title: '📦 Storage',
                  services: ['s3', 'ecr', 'backup']
                },
                database: {
                  title: '🗄️ Database',
                  services: ['rds', 'dynamodb', 'elasticache']
                },
                messaging: {
                  title: '📬 Messaging & Streaming',
                  services: ['sns', 'sqs', 'eventbridge', 'kinesis', 'msk']
                },
                network: {
                  title: '🌐 Networking',
                  services: ['vpc', 'elb', 'apigateway', 'cloudfront']
                },
                security: {
                  title: '🔒 Security',
                  services: ['guardduty', 'waf', 'secretsmanager', 'parameterstore', 'config', 'acm', 'securityhub', 'accessanalyzer', 'inspector', 'kms']
                },
                monitoring: {
                  title: '📊 Monitoring',
                  services: ['cloudwatch']
                },
                analytics: {
                  title: '📈 Analytics & Data Processing',
                  services: ['glue', 'mwaa', 'opensearch', 'appflow']
                },
                migration: {
                  title: '🔁 Migration & Transfer',
                  services: ['transfer', 'dms']
                },
                devops: {
                  title: '🚀 DevOps & CI/CD',
                  services: ['codepipeline']
                }
              };

              // Keys actually rendered as tiles (services with data), in the
              // same order the groups emit them. Drives the summary strip counts
              // so they always match the tiles below.
              const renderedKeys = [];
              Object.values(serviceGroups).forEach(group => {
                serviceOrder
                  .filter(s => group.services.includes(s.key) && s.hasData)
                  .forEach(s => { if (!renderedKeys.includes(s.key)) renderedKeys.push(s.key); });
              });

              const infraSummary = computeInfraSummary(
                accountData, renderedKeys, getServiceHealth, getServiceCost
              );

              const passesFilter = (key) => matchesInfraFilter(
                key,
                serviceDisplayName(key),
                getServiceHealth(key).status,
                { search: infraSearch, filter: infraFilter }
              );

              // Does any rendered tile survive the current search + filter? Used
              // to show a light "no matches" note when everything is filtered out.
              const anyMatch = renderedKeys.some(passesFilter);

              const groupNodes = Object.entries(serviceGroups).map(([groupKey, group]) => {
                // Resolve the batch(es) this group's services belong to via the
                // module-level inverse map, then look up each batch's status.
                const groupBatches = Array.from(new Set(
                  group.services
                    .map(key => INFRA_SERVICE_TO_BATCH[key])
                    .filter(Boolean)
                ));
                const batchStates = groupBatches.map(b => infraBatchStatus[b]);
                // While any of this group's batches is still pending, show the
                // existing per-service loading placeholder for the group.
                const isGroupLoading = batchStates.some(s => s === 'loading');

                if (isGroupLoading) {
                  return (
                    <div key={groupKey} className="service-group">
                      <h4 className="service-group-title">{group.title}</h4>
                      <div className="infra-loading-services-grid">
                        {group.services.map(key => (
                          <div key={key} className="loading-service-card">
                            <span className="service-icon"><AwsServiceIcon service={key} size={28} /></span>
                            <span className="service-name">{key}</span>
                            <span className="service-status loading"></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Batch(es) done/error: render real tiles for services with data.
                // done+empty and error groups fall through the hasData filter and
                // render nothing, exactly as before.
                const groupServices = serviceOrder.filter(s => 
                  group.services.includes(s.key) && s.hasData && passesFilter(s.key)
                );

                // Only show group if it has services with data that pass the filter
                if (groupServices.length === 0) return null;

                return (
                  <div key={groupKey} className="service-group">
                    <h4 className="service-group-title">{group.title}</h4>
                    <div className="service-tile-grid">
                      {groupServices.map(service => (
                        <ServiceTile
                          key={service.key}
                          serviceKey={service.key}
                          status={getServiceHealth(service.key).status}
                          tileElement={serviceSections[service.key]}
                        />
                      ))}
                    </div>
                  </div>
                );
              });

              return (
                <>
                  <InfraHealthSummary
                    summary={infraSummary}
                    lastUpdated={lastUpdated}
                    loading={loadingInfra && renderedKeys.length === 0}
                  />
                  {(anyMatch || loadingInfra) ? groupNodes : (
                    <div className="infra-no-match" role="status">
                      No services match the current search and filter.
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}
      </section>

      {/* CloudWatch Alarm Management */}
      {currentAccountId && (
        <AlarmManagement 
          teamName={teamName}
          environment={environment}
          accountId={currentAccountId}
          region={selectedRegion}
        />
      )}

      {/* Team-Specific Integration Dashboard - Only shown for teams with configured integrations */}
      {hasTeamIntegrations(toInternalTeamName(teamName), environment) && (
        <section id="sec-ies" className="section-container">
          <IESIntegrationDashboard
            teamName={toInternalTeamName(teamName)}
            environment={environment}
            apiUrl={API_URL}
          />
        </section>
      )}

      {/* Alarm Details Modal - Rendered at root level to avoid z-index issues */}
      {showAlarmDetails && (
        <div className="modal-overlay" onClick={closeAlarmDetails}>
          <div className="modal-content alarm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🚨 Active Alarms - {teamName} {environment.toUpperCase()}</h3>
              <button className="modal-close" onClick={closeAlarmDetails}>×</button>
            </div>
            <div className="modal-body">
              {loadingAlarms ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">Loading alarm details...</div>
                </div>
              ) : alarmDetails?.error ? (
                <div className="error-message">{alarmDetails.error}</div>
              ) : alarmDetails?.alarms && alarmDetails.alarms.length > 0 ? (
                <div className="alarms-list">
                  <div className="alarms-info">
                    <span className="info-badge">Region: {selectedRegion}</span>
                    {(accountData.alarms?.ok != null || accountData.alarms?.alarm != null) && (
                      <span className="info-badge">Total alarms: {((accountData.alarms?.ok || 0) + (accountData.alarms?.alarm || 0)).toLocaleString()}</span>
                    )}
                    <span className="info-badge">In ALARM: {(accountData.alarms?.alarm ?? alarmDetails.alarms.length).toLocaleString()}</span>
                    <span className="info-badge">Updated: {new Date().toLocaleTimeString()}</span>
                  </div>
                  <table className="alarms-table">
                    <thead>
                      <tr>
                        <th>Alarm Name</th>
                        <th>State</th>
                        <th>Reason</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alarmDetails.alarms.map((alarm, index) => (
                        <tr key={index} className={`alarm-row alarm-state-${alarm.state.toLowerCase()}`}>
                          <td className="alarm-name">{alarm.name}</td>
                          <td>
                            <span className={`state-badge state-${alarm.state.toLowerCase()}`}>
                              {alarm.state}
                            </span>
                          </td>
                          <td className="alarm-reason">{alarm.reason || 'N/A'}</td>
                          <td className="alarm-time">{new Date(alarm.stateUpdatedTimestamp).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-alarms">No alarms in ALARM state</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-refresh" onClick={fetchAlarmDetails}>
                🔄 Refresh
              </button>
              <button className="btn-close" onClick={closeAlarmDetails}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDetail;
